import {
  Button, Form, SidebarModal,
} from '@domain-group/suit-theme'

import useTemplatePagePartial, { useTemplatePagePartialForm } from '@hooks/useTemplatePagePartial'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import EntitySelector from '@components/EntitySelector'
import OrganizationSelector from '@components/OrganizationSelector'

const MODAL_KEY = 'CreateTemplatePagePartialModal'

type ModalCallbacks = {
  closeModal: () => void,
  createTemplatePagePartial: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  requestOptions?: EntityOptions,
  showModal: boolean,
}

const CreateTemplatePagePartialModal = (props: ModalProps) => {
  const { callbacks, showModal, templatePagePartial: initTemplatePagePartial } = props
  const { closeModal, createTemplatePagePartial } = callbacks || {}

  const {
    callbacks: { createTemplatePagePartial: createFn },
    creating,
    templatePagePartial,
  } = useTemplatePagePartial(initTemplatePagePartial)

  const {
    entityState,
    entityState: {
      brand_id,
      designer_id,
      designer_type,
      short_name,
      measurement_unit,
      width,
      height,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useTemplatePagePartialForm(templatePagePartial, { validateOn: 'change' })

  // To be used with handleCallbackAction
  const createTemplatePagePartialPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Template Page Partial Created',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Create" titleSecondLine="Template Page Partial" />

      <SidebarModal.Content>
        <Form>
          <EntitySelector
            callbacks={{
              selectItem: brand => setEntityState({
                brand_id: brand?.id,
              }),
            }}
            label="Brand *"
            entityKey="brands"
            selectedItemId={brand_id}
          />
        </Form>

        <Form.Field label="* Designer Type" marginTop="large">
          <select
            name="designer_type"
            value={designer_type}
            onChange={e => setEntityState({ designer_id: undefined, designer_type: e.target.value })}
          >
            <option value="">Please Select...</option>
            <option value="Brand">Brand</option>
            <option value="Provider">Provider</option>
          </select>
        </Form.Field>

        <Form>
          {!!designer_type && (
            <OrganizationSelector
              callbacks={{
                selectItem: ({ entity }) => setEntityState({ designer_id: entity?.id }),
              }}
              entityType={designer_type}
              label="* Designer"
              marginTop="large"
              selectedItemId={designer_id}
            />
          )}
        </Form>

        <Form>
          <Form.Field label="* Partial Title" errorMessage={errors.title} marginTop="large">
            <input
              name="title"
              type="text"
              value={title}
              {...handlers}
            />
          </Form.Field>
        </Form>

        <Form>
          <Form.Field label="* Display Name" errorMessage={errors.short_name} marginTop="large">
            <input
              name="short_name"
              type="text"
              value={short_name}
              {...handlers}
            />
          </Form.Field>
        </Form>

        <Form.Row>
          <Form>
            <Form.Field label="* Width" errorMessage={errors.width} halfWidth marginTop="large">
              <input
                name="width"
                type="text"
                value={width}
                {...handlers}
              />
            </Form.Field>
          </Form>

          <Form>
            <Form.Field label="* Height" errorMessage={errors.height} halfWidth marginTop="large">
              <input name="height" type="text" value={height} {...handlers} />
            </Form.Field>
          </Form>
        </Form.Row>

        <Form.Field errorMessage={errors.measurement_unit} label="* Measurement" marginTop="large">
          <select
            name="measurement_unit"
            value={measurement_unit}
            onChange={e => setEntityState({
              measurement_unit: e.target.value,
            })}
          >
            <option value="">Please Select...</option>
            <option value="mm">Millimetres</option>
            <option value="px">Pixels</option>
          </select>
        </Form.Field>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          loadingBubbleColor="white"
          onClick={() => createTemplatePagePartial(createTemplatePagePartialPayload)}
          size="large"
        >
          {saveEnabled ? 'Create Template Partial' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateTemplatePagePartialModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
